import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import {
  assignRoleService,
  removeRoleService,
  getAllUsersWithRolesService,
  getDistinctSectionsService,
  getDistinctHostelsService,
  getDistinctLocationsService,
} from "../services/api-service";
import { Shield, Users, Trash2, AlertCircle, Loader } from "lucide-react";

const RoleManagementPanel = () => {
  const [usersWithRoles, setUsersWithRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("assign");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [locations, setLocations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [assignForm, setAssignForm] = useState({
    role_type: "CLASS_TEACHER",
    room_number: "",
    location: "",
    hostel_name: "",
    boarding_type_filter: "",
  });

  useEffect(() => {
    loadUsersWithRoles();
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    try {
      setLoadingDropdowns(true);
      const locationsList = await getDistinctLocationsService();
      setLocations(locationsList);
      const sectionsList = await getDistinctSectionsService();
      setRooms(sectionsList);
      const hostelsList = await getDistinctHostelsService();
      setHostels(hostelsList);
    } catch (error) {
      console.error("❌ Error loading dropdown data:", error);
      toast.warning("Could not load some dropdown options");
    } finally {
      setLoadingDropdowns(false);
    }
  };

  const loadUsersWithRoles = async () => {
    try {
      setLoading(true);
      const response = await getAllUsersWithRolesService();
      const usersArray = response.data.users || [];
      setUsersWithRoles(usersArray);
    } catch (error) {
      console.error("❌ Error loading users:", error);
      toast.error("Failed to load users with roles");
      setUsersWithRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (e) => {
    setSelectedUserId(e.target.value);
  };

  const handleAssignRole = async (e) => {
    e.preventDefault();

    if (!selectedUserId || selectedUserId === "") {
      toast.error("❌ Please select a user!");
      return;
    }

    if (assignForm.role_type !== "IT_ADMIN" && !assignForm.location.trim()) {
      toast.error("❌ Please select location");
      return;
    }

    if (assignForm.role_type === "CLASS_TEACHER" && !assignForm.location.trim()) {
      toast.error("❌ Please select location for CLASS_TEACHER");
      return;
    }

    if (assignForm.role_type === "HOSTEL_WARDEN" && !assignForm.hostel_name.trim()) {
      toast.error("❌ Please select hostel name");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        user_id: parseInt(selectedUserId),
        role_type: assignForm.role_type,
        room_number: null,
        location: assignForm.role_type === "IT_ADMIN" ? null : assignForm.location,
        hostel_name: assignForm.role_type === "HOSTEL_WARDEN" ? assignForm.hostel_name : null,
        boarding_type_filter: assignForm.boarding_type_filter || null,
      };

      const response = await assignRoleService(payload);

      if (response.status === 200 || response.status === 201) {
        toast.success("✅ Role assigned successfully!");
        resetForm();
        loadUsersWithRoles();
      }
    } catch (error) {
      console.error("❌ Error:", error);
      toast.error(error.response?.data?.detail?.[0]?.msg || "Failed to assign role");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedUserId("");
    setAssignForm({
      role_type: "CLASS_TEACHER",
      room_number: "",
      location: "",
      hostel_name: "",
      boarding_type_filter: "",
    });
  };

  const handleRemoveRole = async (userId) => {
    try {
      setLoading(true);
      const response = await removeRoleService(userId);
      toast.success("✅ Role removed!");
      setDeleteConfirm(null);
      loadUsersWithRoles();
    } catch (error) {
      toast.error("Failed to remove role");
    } finally {
      setLoading(false);
    }
  };

  const isLocationRequired = assignForm.role_type !== "IT_ADMIN";

  const styles = {
    container: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f0f4f8 100%)",
      padding: "24px",
      fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
    },
    maxWidth: {
      maxWidth: "1200px",
      margin: "0 auto",
    },
    header: {
      marginBottom: "32px",
      textAlign: "center",
    },
    headerTitle: {
      fontSize: "36px",
      fontWeight: 700,
      color: "#1a202c",
      margin: "0 0 8px 0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
    },
    headerSubtitle: {
      fontSize: "15px",
      color: "#64748b",
      margin: 0,
    },
    tabsContainer: {
      display: "flex",
      gap: "12px",
      marginBottom: "24px",
      borderBottom: "2px solid #e2e8f0",
      paddingBottom: "0",
    },
    tabButton: (isActive) => ({
      padding: "12px 24px",
      fontSize: "14px",
      fontWeight: 700,
      border: "none",
      cursor: "pointer",
      background: isActive ? "linear-gradient(135deg, #4f9fdd 0%, #357abd 100%)" : "white",
      color: isActive ? "white" : "#64748b",
      borderRadius: "8px 8px 0 0",
      transition: "all 0.3s ease",
      boxShadow: isActive ? "0 -4px 12px rgba(79, 159, 221, 0.2)" : "none",
      borderBottom: isActive ? "3px solid #4f9fdd" : "none",
    }),
    card: {
      background: "white",
      borderRadius: "16px",
      padding: "40px",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.08)",
      border: "1px solid #e2e8f0",
    },
    formContainer: {
      maxWidth: "600px",
      margin: "0 auto",
    },
    sectionTitle: {
      fontSize: "20px",
      fontWeight: 700,
      color: "#1a202c",
      marginBottom: "24px",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    formGroup: {
      marginBottom: "24px",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    label: {
      fontSize: "16px",
      fontWeight: 700,
      color: "#1a202c",
      letterSpacing: "0.3px",
      backgroundColor: "transparent",
      border: "none",
      padding: "0px",
      display: "block",
      marginBottom: "8px",
    },
    input: {
      padding: "12px 16px",
      borderRadius: "10px",
      border: "2px solid #e2e8f0",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "#f8f9fa",
      color: "#1a202c",
      transition: "all 0.3s ease",
      outline: "none",
      fontWeight: 500,
      width: "100%",
      boxSizing: "border-box",
    },
    inputFocus: {
      borderColor: "#4f9fdd",
      background: "white",
      boxShadow: "0 0 0 4px rgba(79, 159, 221, 0.15)",
    },
    helperText: {
      fontSize: "12px",
      color: "#64748b",
      marginTop: "4px",
      fontWeight: 500,
    },
    warningBox: {
      backgroundColor: "#fef3c7",
      border: "1px solid #fcd34d",
      borderRadius: "8px",
      padding: "12px",
      marginBottom: "16px",
      color: "#92400e",
      fontSize: "13px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    infoBox: {
      background: "linear-gradient(135deg, #dbeafe 0%, #e0f2fe 100%)",
      border: "1px solid #7dd3fc",
      borderRadius: "10px",
      padding: "16px",
      marginTop: "24px",
      fontSize: "13px",
      color: "#0369a1",
      lineHeight: 1.6,
    },
    button: {
      padding: "12px 32px",
      borderRadius: "10px",
      fontSize: "14px",
      fontWeight: 700,
      border: "none",
      cursor: "pointer",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      marginTop: "24px",
      width: "100%",
    },
    btnSubmit: {
      background: "linear-gradient(135deg, #4f9fdd 0%, #357abd 100%)",
      color: "white",
      boxShadow: "0 10px 30px rgba(79, 159, 221, 0.3)",
    },
    btnSubmitHover: {
      transform: "translateY(-2px)",
      boxShadow: "0 16px 40px rgba(79, 159, 221, 0.4)",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "14px",
    },
    thead: {
      background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
      borderBottom: "2px solid #e2e8f0",
    },
    th: {
      padding: "14px",
      textAlign: "left",
      fontWeight: 700,
      color: "#334155",
      fontSize: "12px",
      textTransform: "uppercase",
      letterSpacing: "0.3px",
    },
    tr: {
      borderBottom: "1px solid #e2e8f0",
      transition: "all 0.2s ease",
    },
    trHover: {
      backgroundColor: "#f8fafc",
    },
    td: {
      padding: "14px",
      color: "#1a202c",
    },
    roleCell: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 12px",
      borderRadius: "6px",
      fontSize: "12px",
      fontWeight: 600,
      background: "#f3f4f6",
    },
    btnRemove: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 12px",
      background: "#fee2e2",
      color: "#dc2626",
      border: "none",
      borderRadius: "6px",
      fontSize: "12px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    btnRemoveHover: {
      background: "#dc2626",
      color: "white",
    },
    emptyState: {
      textAlign: "center",
      padding: "48px 24px",
      color: "#94a3b8",
    },
    emptyIcon: {
      marginBottom: "12px",
      opacity: 0.3,
    },
    modal: {
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.5)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 50,
      padding: "16px",
    },
    modalContent: {
      background: "white",
      borderRadius: "12px",
      boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
      maxWidth: "400px",
      width: "100%",
      padding: "32px 24px",
    },
    modalIcon: {
      width: "48px",
      height: "48px",
      background: "#fee2e2",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 16px",
      color: "#dc2626",
    },
    modalTitle: {
      fontSize: "20px",
      fontWeight: 700,
      color: "#1a202c",
      margin: "0 0 8px 0",
      textAlign: "center",
    },
    modalDescription: {
      fontSize: "14px",
      color: "#64748b",
      margin: "0 0 24px 0",
      textAlign: "center",
      lineHeight: 1.5,
    },
    modalActions: {
      display: "flex",
      gap: "12px",
    },
    btnCancel: {
      flex: 1,
      padding: "10px 16px",
      background: "white",
      color: "#64748b",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    btnDelete: {
      flex: 1,
      padding: "10px 16px",
      background: "#dc2626",
      color: "white",
      border: "none",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
  };

  const renderAssignTab = () => (
    <div style={styles.formContainer}>
      <h3 style={styles.sectionTitle}>
        <Shield size={24} color="#4f9fdd" />
        Assign Role to User
      </h3>

      {loadingDropdowns && (
        <div style={styles.warningBox}>
          <Loader size={16} />
          Loading location and room data...
        </div>
      )}

      <form onSubmit={handleAssignRole}>
        {/* User Selection */}
        <div style={styles.formGroup}>
          <label style={styles.label}>
            Select User * ({usersWithRoles.length} available)
          </label>

          {usersWithRoles.length === 0 ? (
            <div style={{
              ...styles.warningBox,
              background: "#fee2e2",
              borderColor: "#fca5a5",
              color: "#991b1b",
              marginBottom: "16px",
            }}>
              <AlertCircle size={16} />
              No users found! Add users first.
            </div>
          ) : null}

          <select
            value={selectedUserId}
            onChange={handleUserSelect}
            style={{...styles.input, appearance: "none", paddingRight: "40px", backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234f9fdd' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center", backgroundSize: "20px"}}
            onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
            onBlur={(e) => {
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.background = "#f8f9fa url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234f9fdd' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\") no-repeat right 14px center";
              e.target.style.backgroundSize = "20px";
              e.target.style.boxShadow = "none";
            }}
            required
          >
            <option value="">-- SELECT A USER --</option>
            {usersWithRoles.map((user) => (
              <option key={user.user_id} value={user.user_id}>
                {user.name} ({user.email}) [ID: {user.user_id}]
              </option>
            ))}
          </select>

          {selectedUserId && (
            <small style={{ ...styles.helperText, color: "#10b981" }}>
              ✅ Selected User ID: {selectedUserId}
            </small>
          )}
        </div>

        {/* Role Type */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Role Type *</label>
          <select
            value={assignForm.role_type}
            onChange={(e) => setAssignForm({ ...assignForm, role_type: e.target.value })}
            style={{...styles.input, appearance: "none", paddingRight: "40px", backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234f9fdd' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center", backgroundSize: "20px"}}
            onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
            onBlur={(e) => {
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.background = "#f8f9fa url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234f9fdd' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\") no-repeat right 14px center";
              e.target.style.backgroundSize = "20px";
              e.target.style.boxShadow = "none";
            }}
            required
          >
            <option value="IT_ADMIN">🔐 IT Admin (Full Access)</option>
            <option value="CLASS_TEACHER">👨‍🏫 Management</option>
            {/* <option value="HOSTEL_WARDEN">🏠 Hostel Warden</option> */}
            {/* <option value="LAB_INCHARGE">🧪 Lab In-Charge</option> */}
          </select>

          <small style={styles.helperText}>
            {assignForm.role_type === "IT_ADMIN" && "🔓 Admin has full access to all data"}
            {assignForm.role_type === "Management" && "📍 Can only see data for assigned location"}
            {/* {assignForm.role_type === "HOSTEL_WARDEN" && "🏠 Can only see hostel students"} */}
            {/* {assignForm.role_type === "LAB_INCHARGE" && "🧪 Can only see data for assigned lab"} */}
          </small>
        </div>

        {/* Location Dropdown */}
        {isLocationRequired && (
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Location / Building * ({locations.length} available)
            </label>
            <select
              value={assignForm.location}
              onChange={(e) => setAssignForm({ ...assignForm, location: e.target.value })}
              style={{...styles.input, appearance: "none", paddingRight: "40px", backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234f9fdd' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center", backgroundSize: "20px"}}
              onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
              onBlur={(e) => {
                e.target.style.borderColor = "#e2e8f0";
                e.target.style.background = "#f8f9fa url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234f9fdd' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\") no-repeat right 14px center";
                e.target.style.backgroundSize = "20px";
                e.target.style.boxShadow = "none";
              }}
              required={isLocationRequired}
            >
              <option value="">-- SELECT LOCATION --</option>
              {locations.length > 0 ? (
                locations.map((loc, idx) => (
                  <option key={idx} value={loc}>
                    📍 {loc}
                  </option>
                ))
              ) : (
                <option disabled>No locations available</option>
              )}
            </select>
            {locations.length === 0 && (
              <small style={{ ...styles.helperText, color: "#dc2626" }}>
                ⚠️ No locations found. Add cameras first.
              </small>
            )}
          </div>
        )}

        {/* Hostel Name Dropdown */}
        {assignForm.role_type === "HOSTEL_WARDEN" && (
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Hostel Name * ({hostels.length} available)
            </label>
            <select
              value={assignForm.hostel_name}
              onChange={(e) => setAssignForm({ ...assignForm, hostel_name: e.target.value })}
              style={{...styles.input, appearance: "none", paddingRight: "40px", backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234f9fdd' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center", backgroundSize: "20px"}}
              onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
              onBlur={(e) => {
                e.target.style.borderColor = "#e2e8f0";
                e.target.style.background = "#f8f9fa url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234f9fdd' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e\") no-repeat right 14px center";
                e.target.style.backgroundSize = "20px";
                e.target.style.boxShadow = "none";
              }}
              required
            >
              <option value="">-- SELECT HOSTEL --</option>
              {hostels.length > 0 ? (
                hostels.map((hostel, idx) => (
                  <option key={idx} value={hostel}>
                    🏠 {hostel}
                  </option>
                ))
              ) : (
                <option disabled>No hostels available</option>
              )}
            </select>
            {hostels.length === 0 && (
              <small style={{ ...styles.helperText, color: "#dc2626" }}>
                ⚠️ No hostels found. Add hostel students first.
              </small>
            )}
          </div>
        )}

        {/* Info Box */}
        <div style={styles.infoBox}>
          <strong>ℹ️ Permission Levels:</strong>
          <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
            <li><strong>IT_ADMIN:</strong> Full access to all data</li>
            <li><strong>Management:</strong> Assigned location only</li>
            {/* <li><strong>HOSTEL_WARDEN:</strong> Assigned hostel students</li>
            <li><strong>LAB_INCHARGE:</strong> Assigned lab location</li> */}
          </ul>
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            ...styles.button,
            ...styles.btnSubmit,
            opacity: loading ? 0.7 : 1,
            cursor: loading ? "not-allowed" : "pointer",
          }}
          onMouseEnter={(e) => !loading && Object.assign(e.target.style, styles.btnSubmitHover)}
          onMouseLeave={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 10px 30px rgba(79, 159, 221, 0.3)";
          }}
        >
          {loading ? "⏳ Assigning..." : "✅ Assign Role"}
        </button>
      </form>
    </div>
  );

  const renderUsersTab = () => (
    <div>
      <h3 style={styles.sectionTitle}>
        <Users size={24} color="#4f9fdd" />
        Users & Their Roles
      </h3>

      {usersWithRoles.length === 0 ? (
        <div style={styles.emptyState}>
          <Users size={64} style={styles.emptyIcon} />
          <p>No users with roles assigned</p>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr style={styles.tr}>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Access Scope</th>
                <th style={styles.th}>Location</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {usersWithRoles.map((user) => (
                <tr key={user.user_id} style={styles.tr}>
                  <td style={styles.td}>{user.user_id}</td>
                  <td style={styles.td}><strong>{user.name}</strong></td>
                  <td style={styles.td}>
                    <span style={{ color: "#64748b", fontSize: "13px" }}>
                      {user.email}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={{
                      ...styles.roleCell,
                      background: user.role_type === "IT_ADMIN" ? "#fee2e2" : "#dbeafe",
                      color: user.role_type === "IT_ADMIN" ? "#dc2626" : "#0284c7",
                    }}>
                      {user.role_type === "IT_ADMIN" && "🔐"}
                      {user.role_type === "CLASS_TEACHER" && "👨‍🏫"}
                      {user.role_type === "HOSTEL_WARDEN" && "🏠"}
                      {user.role_type === "LAB_INCHARGE" && "🧪"}
                      {user.role_type}
                    </div>
                  </td>
                  <td style={styles.td}>
                    {user.role_type === "IT_ADMIN" && "🌍 Full Access"}
                    {user.role_type === "CLASS_TEACHER" && `📍 ${user.location}`}
                    {user.role_type === "HOSTEL_WARDEN" && `🏠 ${user.hostel_name}`}
                    {user.role_type === "LAB_INCHARGE" && `🧪 ${user.location}`}
                  </td>
                  <td style={styles.td}>{user.location || "—"}</td>
                  <td style={styles.td}>
                    <button
                      style={styles.btnRemove}
                      onClick={() => setDeleteConfirm(user)}
                      disabled={loading}
                      onMouseEnter={(e) => !loading && Object.assign(e.target.style, styles.btnRemoveHover)}
                      onMouseLeave={(e) => {
                        e.target.style.background = "#fee2e2";
                        e.target.style.color = "#dc2626";
                      }}
                    >
                      <Trash2 size={14} />
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <>
      <style>{`
        select option {
          background: white;
          color: #1a202c;
        }
        table tr:hover {
          background-color: #f8fafc;
        }
      `}</style>

      <div style={styles.container}>
        <div style={styles.maxWidth}>
          {/* Header */}
          <div style={styles.header}>
            <h1 style={styles.headerTitle}>
              <Shield size={40} color="#4f9fdd" />
              Role Management System
            </h1>
            <p style={styles.headerSubtitle}>Assign roles and control user access permissions</p>
          </div>

          {/* Tabs */}
          <div style={styles.tabsContainer}>
            <button
              style={styles.tabButton(activeTab === "assign")}
              onClick={() => setActiveTab("assign")}
              onMouseEnter={(e) => !activeTab.includes("assign") && (e.target.style.background = "#f8fafc")}
              onMouseLeave={(e) => !activeTab.includes("assign") && (e.target.style.background = "white")}
            >
              Assign Role
            </button>
            <button
              style={styles.tabButton(activeTab === "users")}
              onClick={() => setActiveTab("users")}
              onMouseEnter={(e) => !activeTab.includes("users") && (e.target.style.background = "#f8fafc")}
              onMouseLeave={(e) => !activeTab.includes("users") && (e.target.style.background = "white")}
            >
              Users & Roles
            </button>
          </div>

          {/* Content Card */}
          <div style={styles.card}>
            {activeTab === "assign" && renderAssignTab()}
            {activeTab === "users" && renderUsersTab()}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalIcon}>
              <Trash2 size={24} />
            </div>
            <h3 style={styles.modalTitle}>Remove Role?</h3>
            <p style={styles.modalDescription}>
              Remove {deleteConfirm.role_type} role from <strong>{deleteConfirm.name}</strong>?
            </p>
            <div style={styles.modalActions}>
              <button
                style={styles.btnCancel}
                onClick={() => setDeleteConfirm(null)}
                disabled={loading}
                onMouseEnter={(e) => !loading && (e.target.style.background = "#f8fafc")}
                onMouseLeave={(e) => (e.target.style.background = "white")}
              >
                Cancel
              </button>
              <button
                style={{
                  ...styles.btnDelete,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
                onClick={() => handleRemoveRole(deleteConfirm.user_id)}
                disabled={loading}
                onMouseEnter={(e) => !loading && (e.target.style.background = "#b91c1c")}
                onMouseLeave={(e) => (e.target.style.background = "#dc2626")}
              >
                {loading ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RoleManagementPanel;